import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./wordRelease.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";
import TabMenu from "../tabmenu/TabMenu";

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseNumber = (val, fallback) => {
  if (typeof val === "number" && !Number.isNaN(val)) return val;
  const n = Number(val);
  return Number.isNaN(n) ? fallback : n;
};

const WordRelease = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [valuesArray, setValuesArray] = useState([]);
  const [shuffle, setShuffle] = useState([]);
  const [launchpos, setLaunchpos] = useState(0);
  const [numlimit, setNumlimit] = useState(2);
  const [maxvalue, setMaxvalue] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setValuesArray(parseArray(data.values_array));
    setShuffle(parseArray(data.shuffle));
    setLaunchpos(parseNumber(data.launchpos, 0));
    setNumlimit(parseNumber(data.numlimit));
    setMaxvalue(parseNumber(data.maxvalue, 0));
  }, [data]);

  const numlimitSafe = numlimit;
  const maxvalue1 = maxvalue;
  const totalCount = maxvalue1;
  const launchStatus = launchpos * numlimitSafe;
  const releasedCount = Math.min(launchStatus, totalCount);
  const allReleased = releasedCount >= totalCount;

  const releasedCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < releasedCount; i++) {
      const shuffleIdx = shuffle[i];
      const idx = typeof shuffleIdx === "string" ? parseInt(shuffleIdx, 10) - 1 : (shuffleIdx - 1);
      const value = valuesArray[idx];
      cells.push(value != null ? String(value) : "Missing Value");
    }
    return cells;
  }, [shuffle, valuesArray, releasedCount]);

  const allCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < totalCount; i++) {
      const shuffleIdx = shuffle[i];
      const idx = typeof shuffleIdx === "string" ? parseInt(shuffleIdx, 10) - 1 : (shuffleIdx - 1);
      const value = valuesArray[idx];
      cells.push(value != null ? String(value) : "Missing Value");
    }
    return cells;
  }, [shuffle, valuesArray, totalCount]);

  const renderTable = (cells, total) => {
    // Set a reasonable maximum number of columns to prevent horizontal scroll
    const maxCols = 6;
    const cols = Math.min(numlimitSafe, maxCols);
    const rows = [];
    for (let r = 0; r < cells.length; r += cols) {
      const rowCells = cells.slice(r, r + cols);
      rows.push(
        <tr key={r}>
          {rowCells.map((cell, c) => (
            <td key={`${r}-${c}`}>{cell}</td>
          ))}
        </tr>
      );
    }
    if (rows.length === 0 && total > 0) {
      rows.push(
        <tr key="empty">
          <td colSpan={cols}>No data</td>
        </tr>
      );
    }
    return rows;
  };

  const handleRelease = async () => {
    if (allReleased) return;
    const newLaunchpos = launchpos + 1;
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { launchpos: newLaunchpos },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Numbers/Words released successfully.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to release.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleShuffle = async () => {
    const arr = [...shuffle];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { shuffle: arr },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Shuffle successful.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to shuffle.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetLaunch = async () => {
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: { launchpos: 0 },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Launch position reset.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to reset.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="wordrelease-page">
        <div className="wordrelease-loading">Loading theme data...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button
          className="back-button"
          onClick={() => navigate("/admin/rules")}
          type="button"
        >
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <TabMenu />

      <div className="wordrelease-page">
        <div className="wordrelease-cards">
          <div className="wordrelease-card">
            <div className="wordrelease-card-header">
              <i className="fa-solid fa-list"></i> Release Numbers / Words
            </div>
            <div className="wordrelease-card-body">
              <div className="wordrelease-table-wrap">
                <table className="wordrelease-table">
                  <tbody>{renderTable(releasedCells, releasedCount)}</tbody>
                </table>
              </div>
              <div className="wordrelease-actions">
                {allReleased ? (
                  <div className="wordrelease-well wordrelease-well-warn">
                    All {maxvalue1} Number/words Released.
                  </div>
                ) : (
                  <button
                    type="button"
                    className="wordrelease-btn wordrelease-btn-release"
                    onClick={handleRelease}
                    disabled={loading}
                  >
                    Release {numlimitSafe} Numbers or Words
                  </button>
                )}
                <button
                  type="button"
                  className="wordrelease-btn wordrelease-btn-reset"
                  onClick={handleResetLaunch}
                  disabled={loading}
                >
                  Reset Launch
                </button>
              </div>
            </div>
          </div>

          <div className="wordrelease-card">
            <div className="wordrelease-card-header">
              <i className="fa-solid fa-layer-group"></i> Numbers or words to be Released
            </div>
            <div className="wordrelease-card-body">
              <div className="wordrelease-table-wrap">
                <table className="wordrelease-table">
                  <tbody>{renderTable(allCells, totalCount)}</tbody>
                </table>
              </div>
              <div className="wordrelease-actions">
                <button
                  type="button"
                  className="wordrelease-btn wordrelease-btn-shuffle"
                  onClick={handleShuffle}
                  disabled={loading}
                >
                  Shuffle
                </button>
                <div className="wordrelease-well wordrelease-well-info">
                  Do Not Shuffle after Releasing numbers
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WordRelease;
